// ---------------------------------------------------------------------------
// One player's score, as it appears in the reveal's standing and on the end
// screen.
//
// The identity comes from `PlayerChip` rather than from a bare name, so the
// handle slot survives into the standing: a display name shaped like somebody
// else's handle must not read as that player anywhere in the game.
//
// The number and its `+n` are `ScoreTotal`, shared with the reveal's team rows
// so a team total and a player total can never animate differently on a screen
// that shows both.
//
// **`sm` is not decoration.** Nine seated players is an ordinary room, and nine
// `lg` rows are 600px of a 844px phone before the frame around them. The row
// scales down rather than the list scrolling, because a standing you have to
// scroll is a standing you cannot read at a glance.
//
// **The wash marks YOU, not the leader.** It used to mark whoever was top, and
// that is the one row a standing does not need help finding: the list is sorted,
// so the leader is already the first thing on it, and on a tie the old rule
// washed every tied row at once. The row worth finding at a glance is your own —
// it is the only one whose position you cannot predict, and it is the reason
// anybody looks at a standing mid-game. So `leader` is gone rather than demoted:
// two washes on a nine-row list on a phone is noise, and the second one would
// need a colour the palette has not solved for. Nothing replaced it, because
// sort order already says who is winning.
//
// A viewer with no row here — a spectator — simply gets no wash, which is the
// truth: none of these rows is theirs.
//
// The border is on every row, transparent on the ones that are not yours. A
// border that only exists while a row is marked is two pixels of height that
// appear and disappear as the standing re-sorts, and it moves every row
// underneath.
// ---------------------------------------------------------------------------

import type { ReactElement } from 'react';
import type { PublicPlayer } from '@countdown/shared';
import { PlayerChip } from './PlayerChip.js';
import { ScoreTotal } from './ScoreTotal.js';

export type ScoreRowSize = 'sm' | 'md' | 'lg';

const PADDING: Record<ScoreRowSize, string> = {
  sm: 'gap-3 px-3 py-2 sm:py-2.5',
  md: 'gap-3.5 px-3.5 py-3 sm:px-4 sm:py-3.5',
  lg: 'gap-3.5 px-3.5 py-3 sm:px-4 sm:py-4',
};

const REASON_SIZE: Record<ScoreRowSize, string> = {
  sm: 'text-[11px]',
  md: 'text-[12px]',
  lg: 'text-[12px]',
};

export function ScoreRow(props: {
  player: PublicPlayer;
  /**
   * The colour to draw the player in, from `displayColorOf`. Left to the chip's
   * own fallback when it is not given, but a caller with the state in hand
   * should pass it: while teams are on, the team owns the colour.
   *
   * Null is what `displayColorOf` returns for a room past `COLORED_SEATS`, where
   * nobody has one. It is passed straight through and the chip draws no dot —
   * see the prop's note there.
   */
  color?: string | null;
  total: number;
  /** Animated `+n` chip in accent orange. Omit when nothing was gained. */
  gain?: number | null;
  reason?: string;
  /**
   * `alert` for a rejected word, so "STORING rejected" does not read as a
   * neutral note alongside "GROAN, 5 letters". The reveal is the one place a
   * score of zero has to look like a ruling rather than a result.
   */
  reasonTone?: 'default' | 'alert';
  /**
   * The viewer's own row, which is the one that gets the blue wash. There is no
   * `leader`: see the note at the top of the file for why the lead is carried by
   * sort order alone now.
   */
  you?: boolean;
  size?: ScoreRowSize;
  /**
   * Run the total up from `total - gain`. Passed straight to `ScoreTotal` — see
   * the note there for when a caller would turn it off, and why no screen in the
   * game currently does.
   */
  animate?: boolean;
  /**
   * Passed to the chip. The reveal turns it off for its whole standing: a
   * leaderboard scrolling past is not where anybody decides who they are
   * playing with, and duplicate display names are suffixed by the engine, so
   * the handle disambiguates nothing there. Defaults on everywhere else.
   */
  showIdentity?: boolean;
}): ReactElement {
  const {
    player,
    color,
    total,
    gain,
    reason,
    reasonTone = 'default',
    you = false,
    size = 'md',
    animate = true,
    showIdentity = true,
  } = props;

  return (
    <div
      className={`flex items-center rounded-card border transition-colors duration-300 ${
        PADDING[size]
      } ${you ? 'border-blue-500/60 bg-blue-700/35' : 'border-transparent bg-surface-2'}`}
      data-you={you ? 'true' : undefined}
    >
      {/* `sm` puts the reason on the name's line rather than under it. Stacked,
          a row is 60px and nine of them do not fit a phone; beside the name it
          is 44 and they do. The reason is a recap either way — the reveal showed
          the answer in full a moment ago — so truncating the tail of it costs
          less than a standing you have to scroll. */}
      <div
        className={`flex min-w-0 ${size === 'sm' ? 'flex-1 items-center gap-2' : 'flex-col gap-0.5'}`}
      >
        <PlayerChip
          player={player}
          color={color}
          size={size === 'lg' ? 'lg' : size}
          showIdentity={showIdentity}
        />
        {reason && (
          // The reason yields ALL of the shortfall at `sm`, where it shares a
          // line with the name. Flex splits a shortfall in proportion to what
          // each item asked for, so an ordinary `truncate` on both took the
          // name down with it and a nine-character name came out `Bartho…` on
          // a standing with room to spare. The factor has to be enormous rather
          // than merely large: losing a fraction of a pixel is not a narrower
          // name, it is a whole character of `text-overflow`.
          <span
            className={`min-w-0 shrink-[9999] truncate font-mono transition-colors duration-300 ${
              REASON_SIZE[size]
            } ${reasonTone === 'alert' ? 'text-alert' : you ? 'text-blue-200' : 'text-ink-dim'}`}
          >
            {reason}
          </span>
        )}
      </div>

      <ScoreTotal total={total} gain={gain} size={size} animate={animate} className="ml-auto" />
    </div>
  );
}
